import {
    defineConnectorConstellationDerivation,
    defineConnectorItemCollectionDerivation,
    defineConnectorItemDerivation,
    defineEntityConstellationDerivation,
    defineEntityItemDerivation,
    toEntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_pure/derivation-values';
import { buildLineGuideShape, buildSphereShape } from '@toolproof-core/visualization/_pure/shapes';
import type {
    ConnectorConstellationDerivation,
    ConnectorItemCollectionDerivation,
    EntityConstellationDerivation,
    EntityItemCollectionDerivation,
} from '@toolproof-core/visualization/derivation/_contract/derivation';

import { toNaturalDigitAngle } from '@/adapters/primitive/_pure/derivation/constellations';
import {
    toBooleanDiscItemKey,
    toNaturalComparisonAnchorCollectionKey,
    toNaturalComparisonAnchorConstellationKey,
    toNaturalComparisonAnchorItemKey,
    toNaturalComparisonCellBoundaryAnchorCollectionKey,
    toNaturalComparisonCellBoundaryAnchorConstellationKey,
    toNaturalComparisonCellBoundaryAnchorItemKey,
    toNaturalComparisonCellBoundaryItemKey,
    toNaturalComparisonOrbitCollectionKey,
    toNaturalComparisonOrbitConstellationKey,
    toNaturalComparisonOrbitItemKey,
    toNaturalComparisonRayCollectionKey,
    toNaturalComparisonRayConstellationKey,
    toNaturalComparisonRayItemKey,
    type ComparisonCellBoundary,
    type ComparisonCellBoundaryEdge,
} from '@/adapters/primitive/_pure/derivation/keyCodec';
import type { Config, PrimitiveWheelState } from '@/adapters/primitive/_contract/config';

type ComparisonPlane = {
    xAxis: readonly [number, number, number];
    yAxis: readonly [number, number, number];
};

type ComparisonRayDerivations = {
    entityItemCollectionDerivations: EntityItemCollectionDerivation[];
    entityConstellationDerivations: EntityConstellationDerivation[];
    connectorItemCollectionDerivation: ConnectorItemCollectionDerivation;
    connectorConstellationDerivation: ConnectorConstellationDerivation;
};

function toScanAnchorPosition(args: {
    wheel: PrimitiveWheelState;
    radius: number;
    angle: number;
    plane?: ComparisonPlane;
}): [number, number, number] {
    const xOffset = args.radius * Math.sin(args.angle);
    const yOffset = -args.radius * Math.cos(args.angle);
    const xAxis = args.plane?.xAxis ?? [1, 0, 0];
    const yAxis = args.plane?.yAxis ?? [0, 1, 0];
    return [
        args.wheel.position[0] + xAxis[0] * xOffset + yAxis[0] * yOffset,
        args.wheel.position[1] + xAxis[1] * xOffset + yAxis[1] * yOffset,
        args.wheel.position[2] + xAxis[2] * xOffset + yAxis[2] * yOffset,
    ];
}

function toCellCenterPosition(args: {
    wheel: PrimitiveWheelState;
    power: number;
    slot: number;
    config: Config;
    plane?: ComparisonPlane;
}): [number, number, number] {
    const radius = args.config.base.display.wheel.innerRadius
        + args.power * (args.config.base.display.wheel.ringWidth + args.config.natural.display.wheel.ringGap)
        + args.config.base.display.wheel.ringWidth / 2;
    const angle = toNaturalDigitAngle(args.config, args.slot);
    return toScanAnchorPosition({ wheel: args.wheel, radius, angle, plane: args.plane });
}

export function buildNaturalRayScanningDerivations(
    config: Config,
    wheels: readonly PrimitiveWheelState[],
    plane?: ComparisonPlane,
): ComparisonRayDerivations | null {
    const decision = wheels.find((wheel) => wheel.role === 'less-than-decision');
    const scanPower = decision?.comparisonScanPower;
    const scanSlot = decision?.comparisonScanSlot;
    if (decision === undefined || scanPower === undefined || scanSlot === undefined) return null;

    const sideSpecs = [
        { side: 'source' as const, role: 'less-than-source' as const },
        { side: 'target' as const, role: 'less-than-target' as const },
    ];
    const entityItemCollectionDerivations: EntityItemCollectionDerivation[] = [];
    const entityConstellationDerivations: EntityConstellationDerivation[] = [];
    const connectorItemDerivations = [];
    if (decision.comparisonOrbitRadius !== undefined && decision.comparisonOrbitCenter !== undefined) {
        const itemKey = toNaturalComparisonOrbitItemKey();
        entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
            itemCollectionKey: toNaturalComparisonOrbitCollectionKey(),
            itemDerivations: [defineEntityItemDerivation({
                item: { itemKind: 'entity', itemKey, name: 'LessThan travelling-eye guide' },
                parametricState: {
                    shapePresentation: { appearanceMode: 'outlined', border: {
                        color: config.natural.display.comparison.helperCircle.outlineColor,
                        opacity: 0.35,
                        width: config.natural.display.comparison.helperCircle.outlineWidth,
                    } },
                    visualStyle: { opacity: 0.35 },
                },
            })],
        }));
        entityConstellationDerivations.push(defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity', constellationKey: toNaturalComparisonOrbitConstellationKey(),
                itemCollectionKey: toNaturalComparisonOrbitCollectionKey(),
                anchor: { itemKey, anchorKey: 'center' },
                meta: { wheelRole: 'less-than-decision' },
            },
            parametricState: { layout: {
                layoutKind: 'sector-ring', anchor: { anchorKind: 'position', position: [...decision.comparisonOrbitCenter] },
                innerRadius: decision.comparisonOrbitRadius, outerRadius: decision.comparisonOrbitRadius,
                angularGapFraction: 0, arcSegments: config.base.display.wheel.arcSegments,
                rotation: [Math.PI / 2, 0, 0],
            } },
        }));
    }
    const boundarySpan = (Math.PI * 2 / config.natural.radix) * (1 - config.natural.display.wheel.angularGapFraction);

    for (const spec of sideSpecs) {
        const wheel = wheels.find((candidate) => candidate.role === spec.role);
        if (wheel === undefined) continue;
        let scanPosition = toCellCenterPosition({ wheel, power: scanPower, slot: scanSlot, config, plane });
        const target = wheels.find((candidate) => candidate.role === 'less-than-target');
        if (spec.side === 'source' && target !== undefined) {
            const targetPosition = toCellCenterPosition({ wheel: target, power: scanPower, slot: scanSlot, config, plane });
            const progress = decision.comparisonSourceReachProgress ?? 1;
            scanPosition = [
                targetPosition[0] + (scanPosition[0] - targetPosition[0]) * progress,
                targetPosition[1] + (scanPosition[1] - targetPosition[1]) * progress,
                targetPosition[2] + (scanPosition[2] - targetPosition[2]) * progress,
            ];
        }
        const anchorItemKey = toNaturalComparisonAnchorItemKey({ side: spec.side });
        entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
            itemCollectionKey: toNaturalComparisonAnchorCollectionKey({ side: spec.side }),
            itemDerivations: [defineEntityItemDerivation({
                item: {
                    itemKind: 'entity',
                    itemKey: anchorItemKey,
                    name: `NaturalLessThan ${spec.side} scan anchor`,
                    description: 'Invisible endpoint for the current comparison ray.',
                },
                parametricState: {
                    shape: buildSphereShape({ radius: 0.01 }),
                    visualStyle: { opacity: 0 },
                },
            })],
        }));
        entityConstellationDerivations.push(defineEntityConstellationDerivation({
            constellation: {
                constellationKind: 'entity',
                constellationKey: toNaturalComparisonAnchorConstellationKey({ side: spec.side }),
                itemCollectionKey: toNaturalComparisonAnchorCollectionKey({ side: spec.side }),
                anchor: { itemKey: anchorItemKey, anchorKey: 'center' },
                meta: { wheelRole: wheel.role, power: scanPower },
            },
            parametricState: {
                layout: {
                    layoutKind: 'sector-ring',
                    anchor: {
                        anchorKind: 'position',
                        position: scanPosition,
                    },
                    innerRadius: 0,
                    outerRadius: 0.01,
                    angularGapFraction: 0,
                    arcSegments: config.base.display.wheel.arcSegments,
                    rotation: [0, 0, 0],
                },
            },
        }));

        const ringInnerRadius = config.base.display.wheel.innerRadius
            + scanPower * (config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap);
        const radiusByEdge: Record<ComparisonCellBoundaryEdge, number> = {
            inner: ringInnerRadius - config.natural.display.wheel.ringGap * 0.12,
            outer: ringInnerRadius + config.base.display.wheel.ringWidth + config.natural.display.wheel.ringGap * 0.12,
        };
        const centerAngle = toNaturalDigitAngle(config, scanSlot);
        for (const boundary of ['start', 'end'] as const satisfies readonly ComparisonCellBoundary[]) {
            const angle = centerAngle + (boundary === 'start' ? -boundarySpan / 2 : boundarySpan / 2);
            for (const edge of ['inner', 'outer'] as const satisfies readonly ComparisonCellBoundaryEdge[]) {
                const ref = { side: spec.side, boundary, edge };
                const itemKey = toNaturalComparisonCellBoundaryAnchorItemKey(ref);
                entityItemCollectionDerivations.push(toEntityItemCollectionDerivation({
                    itemCollectionKey: toNaturalComparisonCellBoundaryAnchorCollectionKey(ref),
                    itemDerivations: [defineEntityItemDerivation({
                        item: {
                            itemKind: 'entity',
                            itemKey,
                            name: `NaturalLessThan ${spec.side} ${boundary} cell boundary ${edge} anchor`,
                            description: 'Invisible endpoint for a scanned-cell boundary rail.',
                        },
                        parametricState: {
                            shape: buildSphereShape({ radius: 0.01 }),
                            visualStyle: { opacity: 0 },
                        },
                    })],
                }));
                entityConstellationDerivations.push(defineEntityConstellationDerivation({
                    constellation: {
                        constellationKind: 'entity',
                        constellationKey: toNaturalComparisonCellBoundaryAnchorConstellationKey(ref),
                        itemCollectionKey: toNaturalComparisonCellBoundaryAnchorCollectionKey(ref),
                        anchor: { itemKey, anchorKey: 'center' },
                        meta: { wheelRole: wheel.role, power: scanPower },
                    },
                    parametricState: {
                        layout: {
                            layoutKind: 'sector-ring',
                            anchor: {
                                anchorKind: 'position',
                                position: toScanAnchorPosition({
                                    wheel,
                                    radius: radiusByEdge[edge],
                                    angle,
                                    plane,
                                }),
                            },
                            innerRadius: 0,
                            outerRadius: 0.01,
                            angularGapFraction: 0,
                            arcSegments: config.base.display.wheel.arcSegments,
                            rotation: [0, 0, 0],
                        },
                    },
                }));
            }
            connectorItemDerivations.push(defineConnectorItemDerivation({
                item: {
                    itemKind: 'connector',
                    itemKey: toNaturalComparisonCellBoundaryItemKey({ side: spec.side, boundary }),
                    shape: buildLineGuideShape(),
                    endpoints: {
                        from: {
                            endpointKind: 'static',
                            itemKey: toNaturalComparisonCellBoundaryAnchorItemKey({
                                side: spec.side,
                                boundary,
                                edge: 'inner',
                            }),
                            anchorKey: 'center',
                        },
                        to: {
                            endpointKind: 'static',
                            itemKey: toNaturalComparisonCellBoundaryAnchorItemKey({
                                side: spec.side,
                                boundary,
                                edge: 'outer',
                            }),
                            anchorKey: 'center',
                        },
                    },
                },
                parametricState: {
                    visualStyle: {
                        color: config.natural.display.comparison.rayColor,
                        opacity: decision.comparisonRaysVisible && (spec.side === 'target' || decision.comparisonRayReach === 'source')
                            ? config.natural.display.comparison.rayOpacity
                            : 0,
                    },
                    depthTest: false,
                },
            }));
        }
        connectorItemDerivations.push(defineConnectorItemDerivation({
            item: {
                itemKind: 'connector',
                itemKey: toNaturalComparisonRayItemKey({ side: spec.side }),
                shape: buildLineGuideShape(),
                endpoints: {
                    from: {
                        endpointKind: 'static',
                        itemKey: spec.side === 'source' ? toNaturalComparisonAnchorItemKey({ side: 'target' }) : toBooleanDiscItemKey({
                            wheelRole: 'less-than-decision',
                            power: 0,
                        }),
                        anchorKey: 'center',
                    },
                    to: { endpointKind: 'static', itemKey: anchorItemKey, anchorKey: 'center' },
                },
            },
            parametricState: {
                visualStyle: {
                    color: config.natural.display.comparison.rayColor,
                    opacity: decision.comparisonRaysVisible && (spec.side === 'target' || decision.comparisonRayReach === 'source')
                        ? config.natural.display.comparison.rayOpacity
                        : 0,
                },
                depthTest: false,
            },
        }));
    }

    if (connectorItemDerivations.length === 0) return null;
    return {
        entityItemCollectionDerivations,
        entityConstellationDerivations,
        connectorItemCollectionDerivation: defineConnectorItemCollectionDerivation({
            itemCollectionKey: toNaturalComparisonRayCollectionKey(),
            itemCollectionKind: 'connector',
            itemDerivations: connectorItemDerivations,
        }),
        connectorConstellationDerivation: defineConnectorConstellationDerivation({
            constellation: {
                constellationKind: 'connector',
                constellationKey: toNaturalComparisonRayConstellationKey(),
                itemCollectionKey: toNaturalComparisonRayCollectionKey(),
            },
            parametricState: { visibility: { visible: true } },
        }),
    };
}
